import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { requestNotificationPermission, onForegroundMessage } from '../firebase-messaging';
import axios from 'axios';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// URL suono onde (royalty free)
const WAVE_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2515/2515-preview.mp3';

const NotificationManager = () => {
  const { token, user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const audioRef = useRef(null);
  
  // Rileva iOS
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent || '');

  // Inizializza audio e permessi
  useEffect(() => {
    // Su iOS non fare nulla con le notifiche push
    if (isIOS) return;
    
    audioRef.current = new Audio(WAVE_SOUND_URL);
    audioRef.current.volume = 0.5;
    
    // Controlla permesso notifiche in modo sicuro
    try {
      if (typeof Notification !== 'undefined' && Notification.permission) {
        setPermission(Notification.permission);
      }
    } catch (e) {
      console.log('Notification API not available');
    }
  }, [isIOS]);

  // Funzione per riprodurre suono onde
  const playWaveSound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play prevented:', e));
    }
  };

  // Aggiorna badge (se supportato)
  const updateBadge = (count) => {
    if ('setAppBadge' in navigator) {
      if (count > 0) {
        navigator.setAppBadge(count).catch(e => console.log('Badge not supported'));
      } else {
        navigator.clearAppBadge().catch(e => console.log('Badge clear not supported'));
      }
    }
  };

  useEffect(() => {
    // Controlla se mostrare il prompt
    const dismissed = localStorage.getItem('notification_prompt_dismissed');
    const hasToken = localStorage.getItem('fcm_token');
    
    if (permission === 'default' && !dismissed && !hasToken && user) {
      // Mostra prompt dopo 5 secondi
      const timer = setTimeout(() => setShowPrompt(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [permission, user]);

  useEffect(() => {
    // Listener per notifiche in foreground
    const unsubscribe = onForegroundMessage((payload) => {
      // Riproduci suono onde
      playWaveSound();
      
      // Aggiorna badge
      const currentBadge = parseInt(localStorage.getItem('notification_badge') || '0');
      const newBadge = currentBadge + 1;
      localStorage.setItem('notification_badge', newBadge.toString());
      updateBadge(newBadge);
      
      // Mostra toast per notifiche ricevute mentre l'app è aperta
      toast(payload.notification?.title || 'Nuova notifica', {
        description: payload.notification?.body,
        duration: 8000,
        icon: <Bell className="w-5 h-5 text-cyan-400" />
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const handleEnableNotifications = async () => {
    setLoading(true);
    
    try {
      const fcmToken = await requestNotificationPermission();
      
      if (fcmToken) {
        // Salva token nel backend
        await axios.post(`${API_URL}/api/notifications/register`, {
          token: fcmToken
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        localStorage.setItem('fcm_token', fcmToken);
        setPermission('granted');
        setShowPrompt(false);
        
        // Riproduci suono di conferma
        playWaveSound();
        
        toast.success('Notifiche attivate! Riceverai aggiornamenti su eventi e novità.');
      } else {
        setPermission(Notification.permission);
        if (Notification.permission === 'denied') {
          toast.error('Permesso negato. Abilita le notifiche dalle impostazioni del browser.');
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Errore nell\'attivazione delle notifiche');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('notification_prompt_dismissed', 'true');
    setShowPrompt(false);
  };

  // Su iOS non mostrare nulla (iOS non supporta Web Push Notifications)
  if (isIOS) return null;
  
  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-300">
      <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-4 shadow-lg shadow-cyan-500/20 max-w-sm mx-auto">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-semibold mb-1">Attiva le notifiche</h4>
            <p className="text-slate-400 text-sm mb-3">
              Ricevi aggiornamenti su eventi, nuove segnalazioni e novità della community!
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleEnableNotifications}
                disabled={loading}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-medium rounded-lg flex items-center gap-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Attiva
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                No grazie
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationManager;
